import React, { useEffect, useState } from "react";
import {
  X,
  Wallet,
  Calendar,
  Save,
  CheckCircle2,
  Info,
  Loader2,
  User,
  Search,
  Building2,
  Landmark,
  Receipt,
  FileText,
  Plus,
  Trash2,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import dayjs from "dayjs";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import {
  createPaymentRequest,
  getPaymentDetail,
} from "../../../api/finance/payment/paymentRequests";
import {
  getAllAdvanceRequests,
  getAdvanceDetail,
} from "../../../api/finance/advance/advanceRequests";

const formatCurrency = (value) => Number(value || 0).toLocaleString("vi-VN");
const newLine = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  description: "",
  amount: "",
});

export default function PaymentCreateModal({
  isOpen,
  onClose,
  onSaved,
  copyFromId = null,
}) {
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const isCopy = Boolean(copyFromId);
  const [loading, setLoading] = useState(false);
  const [loadingCopy, setLoadingCopy] = useState(false);
  const [advanceList, setAdvanceList] = useState([]);
  const [advanceId, setAdvanceId] = useState(null);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [advanceCode, setAdvanceCode] = useState("");
  const [paymentDate, setPaymentDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [lines, setLines] = useState([newLine()]);
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [shouldReset, setShouldReset] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const applyDetailToForm = async (detail) => {
      // Copy = phiếu mới: mặc định hôm nay; vẫn nhận được ngày từ detail nếu muốn đổi tay
      setPaymentDate(dayjs().format("YYYY-MM-DD"));
      setAccountNumber(detail.account_number || "");
      setBankName(detail.bank_name || "");
      setAccountHolder(detail.account_holder || "");

      const detailLines = Array.isArray(detail.lines) ? detail.lines : [];
      setLines(
        detailLines.length
          ? detailLines.map((line) => ({
              id: newLine().id,
              description: line.description || "",
              amount: String(line.amount ?? ""),
            }))
          : [
              {
                id: newLine().id,
                description: detail.payment_reason || "",
                amount: String(detail.payment_amount || ""),
              },
            ]
      );

      const linkedAdvanceId =
        detail.advance_id || detail.advance_request_id || null;
      if (linkedAdvanceId) {
        setAdvanceId(linkedAdvanceId);
        try {
          const adv = await getAdvanceDetail(linkedAdvanceId);
          setAdvanceAmount(Number(adv.advance_amount) || 0);
          setAdvanceCode(adv.request_code || "");
        } catch {
          setAdvanceAmount(Number(detail.advance_amount) || 0);
          setAdvanceCode(detail.advance_request_code || "");
        }
      } else {
        setAdvanceId(null);
        setAdvanceAmount(0);
        setAdvanceCode("");
      }

      setShouldReset(false);
    };

    if (copyFromId) {
      let cancelled = false;
      const load = async () => {
        try {
          setLoadingCopy(true);
          const detail = await getPaymentDetail(copyFromId);
          if (cancelled) return;
          await applyDetailToForm(detail);
        } catch (err) {
          console.error(err);
          showErrorToast(
            err?.response?.data?.message ||
              "Không tải được phiếu để sao chép."
          );
          onClose?.();
        } finally {
          if (!cancelled) setLoadingCopy(false);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }

    if (shouldReset) {
      setAdvanceId(null);
      setAdvanceAmount(0);
      setAdvanceCode("");
      setPaymentDate(dayjs().format("YYYY-MM-DD"));
      setLines([newLine()]);
      setAccountNumber("");
      setBankName("");
      setAccountHolder("");
      setShouldReset(false);
    }
  }, [isOpen, copyFromId, shouldReset, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    getAllAdvanceRequests().then((records) => {
      const list = Array.isArray(records) ? records : [];
      const approved = list.filter((a) => a.status === "approved");
      setAdvanceList(
        approved.map((a) => ({
          value: a.id,
          label: `${a.request_code} - ${a.employee_name} (${Number(
            a.advance_amount
          ).toLocaleString("vi-VN")}đ)`,
        }))
      );
    });
  }, [isOpen]);

  const handleAdvanceChange = async (opt) => {
    setAdvanceId(opt?.value || null);
    if (opt) {
      try {
        const detail = await getAdvanceDetail(opt.value);
        setAdvanceAmount(Number(detail.advance_amount) || 0);
        setAdvanceCode(detail.request_code || opt.label || "");
        const detailLines = Array.isArray(detail.lines) ? detail.lines : [];
        setLines(
          detailLines.length
            ? detailLines.map((line) => ({
                id: line.id || newLine().id,
                description: line.description || "",
                amount: String(line.amount ?? ""),
              }))
            : [
                {
                  id: newLine().id,
                  description: detail.advance_reason || "",
                  amount: String(detail.advance_amount || ""),
                },
              ]
        );
        setBankName(detail.bank_name || "");
        setAccountNumber(detail.account_number || "");
        setAccountHolder(detail.account_holder || "");
      } catch (err) {
        console.error("Lỗi lấy chi tiết tạm ứng:", err);
      }
    } else {
      setAdvanceAmount(0);
      setAdvanceCode("");
      setLines([newLine()]);
    }
  };

  const updateLine = (id, patch) => {
    setLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, ...patch } : line))
    );
  };

  const addLine = () => setLines((prev) => [...prev, newLine()]);

  const removeLine = (id) => {
    setLines((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((line) => line.id !== id);
    });
  };

  const handleCancel = () => {
    if (loading) return;
    setShouldReset(true);
    onClose();
  };

  const handleSave = async () => {
    if (!accountNumber || !bankName) {
      showErrorToast("Vui lòng nhập đầy đủ các thông tin bắt buộc!");
      return;
    }

    const normalizedLines = [];
    for (let i = 0; i < lines.length; i += 1) {
      const description = String(lines[i].description || "").trim();
      const amount = Number(String(lines[i].amount || "").replace(/,/g, ""));
      if (!description) {
        showErrorToast(`Dòng ${i + 1}: vui lòng nhập nội dung thanh toán.`);
        return;
      }
      if (!Number.isFinite(amount) || amount <= 0) {
        showErrorToast(`Dòng ${i + 1}: số tiền phải lớn hơn 0.`);
        return;
      }
      normalizedLines.push({ description, amount });
    }

    try {
      setLoading(true);
      const payload = { 
        advance_id: advanceId, 
        payment_date: paymentDate, 
        lines: normalizedLines,
        account_number: accountNumber,
        bank_name: bankName,
        account_holder: accountHolder
      };
      await createPaymentRequest(payload);
      showSuccessToast("Đã lưu phiếu ở trạng thái Nháp. Mở chi tiết để gửi duyệt.");
      setShouldReset(true);
      onSaved?.();
      onClose();
    } catch (error) {
      showErrorToast(error.message || "Lỗi khi lưu dữ liệu");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedAdvance =
    advanceList.find((item) => item.value === advanceId) || null;
  const numericPaymentAmount = lines.reduce(
    (sum, line) => sum + (Number(String(line.amount || "").replace(/,/g, "")) || 0),
    0
  );
  const filledLines = lines.filter(
    (line) =>
      String(line.description || "").trim() &&
      Number(String(line.amount || "").replace(/,/g, "")) > 0
  ).length;
  const settlementDiff = advanceId ? numericPaymentAmount - advanceAmount : 0;
  const settlementLabel =
    !advanceId
      ? "Không liên kết"
      : settlementDiff > 0
        ? "Chi thêm"
        : settlementDiff < 0
          ? "Thu lại"
          : "Khớp tạm ứng";
  const settlementAmount = advanceId ? Math.abs(settlementDiff) : 0;
  const completionCount = [
    filledLines > 0,
    paymentDate,
    accountNumber,
    bankName,
  ].filter(Boolean).length;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={handleCancel}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-6xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] border border-slate-100"
        >
          {isMobileViewport ? (
            <div className="p-6 flex flex-col items-center justify-center text-center min-h-[280px]">
              <div className="p-3 rounded-2xl bg-red-50 text-red-600 mb-4">
                <FileText size={28} />
              </div>
              <h2 className="text-lg font-black text-slate-800">
                {isCopy
                  ? "Sao chép đề nghị trên máy tính"
                  : "Tạo đề nghị trên máy tính"}
              </h2>
              <p className="text-sm text-slate-500 mt-2 max-w-sm">
                Form tạo đề nghị thanh toán được tối ưu cho desktop. Trên điện
                thoại bạn vẫn có thể xem và phê duyệt.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-6 px-6 py-3 rounded-2xl bg-red-700 text-white text-xs font-black uppercase tracking-wider"
              >
                Đóng
              </button>
            </div>
          ) : (
            <>
          <div className="px-6 md:px-8 py-6 border-b border-slate-100 bg-gradient-to-r from-red-50 via-white to-white flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-200">
                  <Wallet size={24} />
                </div>
                <div className="text-left">
                  <h2 className="text-xl font-black text-slate-800 tracking-tight">
                    {isCopy
                      ? "Sao chép đề nghị thanh toán"
                      : "Tạo đề nghị thanh toán"}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {isCopy
                      ? "Đã nạp dữ liệu từ phiếu gốc — kiểm tra và gửi như phiếu mới."
                      : "Nhập toàn bộ thông tin trên một màn hình để kiểm tra và gửi phiếu nhanh hơn."}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <StatusChip
                  icon={User}
                  label={user?.name || user?.employee_name || "Người tạo phiếu"}
                />
                <StatusChip
                  icon={Building2}
                  label={user?.department_name || "Chưa có phòng ban"}
                />
                <StatusChip
                  icon={CheckCircle2}
                  label={`Hoàn thiện ${completionCount}/4 mục chính`}
                  highlighted
                />
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-red-50 rounded-xl transition-all text-slate-400 hover:text-red-600"
            >
              <X size={22} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6 custom-scrollbar">
            {loadingCopy ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3 text-slate-500">
                <Loader2 size={32} className="animate-spin text-red-600" />
                <p className="text-sm font-medium">Đang tải dữ liệu sao chép...</p>
              </div>
            ) : (
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.55fr)_360px] gap-6 items-start">
              <div className="space-y-6">
                <SectionCard
                  icon={Search}
                  title="Đối chiếu tạm ứng"
                  description="Có thể liên kết với phiếu tạm ứng đã duyệt để hoàn ứng. Khi chọn, app sẽ nạp các hạng mục và tự tính chênh lệch."
                >
                  <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3 mb-5">
                    <Info className="text-amber-600 shrink-0 mt-0.5" size={18} />
                    <p className="text-xs text-amber-800 leading-relaxed font-medium text-left">
                      Chỉ chọn phiếu tạm ứng khi khoản thanh toán này phát sinh từ một đề nghị tạm ứng trước đó.
                    </p>
                  </div>

                  <FieldLabel label="Phiếu tạm ứng liên quan" />
                  <Select
                    options={advanceList}
                    value={selectedAdvance}
                    onChange={handleAdvanceChange}
                    styles={customSelectStyles}
                    placeholder="Tìm mã phiếu hoặc tên nhân viên..."
                    isClearable
                  />

                  {advanceId ? (
                    <div className="mt-5 p-5 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="text-left">
                        <p className="text-xs font-bold text-slate-700 mb-1">
                          {advanceCode || selectedAdvance?.label}
                        </p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                          Số tiền đã tạm ứng
                        </p>
                        <p className="text-xl font-black text-red-600">
                          {formatCurrency(advanceAmount)} ₫
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 text-sm font-bold">
                        <CheckCircle2 size={16} />
                        Đã liên kết phiếu tạm ứng
                      </div>
                    </div>
                  ) : null}
                </SectionCard>

                <SectionCard
                  icon={Receipt}
                  title="Thông tin thanh toán"
                  description="Nhập nhiều hạng mục thực chi. Tổng thanh toán được tự cộng từ từng dòng."
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <FieldLabel label="Ngày chứng từ" required />
                      <div className="relative">
                        <Calendar
                          size={16}
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                        />
                        <input
                          type="date"
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-2xl pl-11 pr-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-5">
                    <FieldLabel label="Các hạng mục thanh toán" required />
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="w-full min-w-[640px] border-collapse">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-3 text-center text-[11px] font-black uppercase tracking-wider text-slate-500 w-14">
                              STT
                            </th>
                            <th className="px-3 py-3 text-left text-[11px] font-black uppercase tracking-wider text-slate-500">
                              Nội dung thanh toán
                            </th>
                            <th className="px-3 py-3 text-right text-[11px] font-black uppercase tracking-wider text-slate-500 w-48">
                              Số tiền
                            </th>
                            <th className="px-3 py-3 text-center text-[11px] font-black uppercase tracking-wider text-slate-500 w-16">
                              Xóa
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((line, index) => (
                            <tr key={line.id} className="border-t border-slate-100">
                              <td className="px-3 py-3 text-center text-sm font-semibold text-slate-500">
                                {index + 1}
                              </td>
                              <td className="px-3 py-3">
                                <input
                                  type="text"
                                  value={line.description}
                                  onChange={(e) =>
                                    updateLine(line.id, { description: e.target.value })
                                  }
                                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                                  placeholder="Nhập hạng mục thanh toán..."
                                />
                              </td>
                              <td className="px-3 py-3">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={
                                    line.amount
                                      ? Number(
                                          String(line.amount).replace(/,/g, "")
                                        ).toLocaleString("vi-VN")
                                      : ""
                                  }
                                  onChange={(e) =>
                                    updateLine(line.id, {
                                      amount: e.target.value
                                        .replace(/,/g, "")
                                        .replace(/[^\d]/g, ""),
                                    })
                                  }
                                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-right font-semibold text-red-700 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                                  placeholder="0"
                                />
                              </td>
                              <td className="px-3 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeLine(line.id)}
                                  disabled={lines.length <= 1}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <button
                        type="button"
                        onClick={addLine}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-red-700 hover:bg-red-100"
                      >
                        <Plus size={16} />
                        Thêm hạng mục
                      </button>
                      <div className="text-right">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                          Tổng thực chi ({lines.length} dòng)
                        </p>
                        <p className="text-xl font-black text-red-700">
                          {formatCurrency(numericPaymentAmount)} ₫
                        </p>
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={Landmark}
                  title="Tài khoản nhận tiền"
                  description="Điền chính xác thông tin tài khoản để tránh sai lệch khi chuyển khoản."
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <FieldLabel label="Số tài khoản" required />
                      <input
                        type="text"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        placeholder="Nhập số tài khoản..."
                        className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div>
                      <FieldLabel label="Ngân hàng" required />
                      <input
                        type="text"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder="Vietcombank, MB, BIDV..."
                        className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <FieldLabel label="Chủ tài khoản" />
                      <input
                        type="text"
                        value={accountHolder}
                        onChange={(e) => setAccountHolder(e.target.value)}
                        placeholder="NGUYEN VAN A"
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-black uppercase outline-none focus:bg-white focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>
                </SectionCard>
              </div>

              <div className="space-y-5 xl:sticky xl:top-0">
                <div className="rounded-[1.75rem] border border-slate-200 bg-slate-900 text-white p-5 shadow-xl shadow-slate-900/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-11 w-11 rounded-2xl bg-white/10 flex items-center justify-center">
                      <User size={20} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">
                        Người tạo phiếu
                      </p>
                      <p className="font-bold text-base">
                        {user?.name || user?.employee_name || "Chưa xác định"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <SummaryRow
                      label="Phòng ban"
                      value={user?.department_name || "Chưa có dữ liệu"}
                      dark
                    />
                    <SummaryRow
                      label="Ngày chứng từ"
                      value={paymentDate || "Chưa chọn"}
                      dark
                    />
                    <SummaryRow
                      label="Phiếu tạm ứng"
                      value={selectedAdvance?.label || "Không liên kết"}
                      dark
                    />
                  </div>
                </div>

                <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Wallet size={18} className="text-red-500" />
                    <h3 className="font-bold text-slate-800">Tóm tắt thanh toán</h3>
                  </div>

                  <div className="rounded-2xl bg-red-50 border border-red-100 px-4 py-4 mb-4">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-red-400">
                      Số tiền thanh toán
                    </p>
                    <p className="text-2xl font-black text-red-700 mt-1">
                      {numericPaymentAmount
                        ? `${formatCurrency(numericPaymentAmount)} ₫`
                        : "0 ₫"}
                    </p>
                  </div>

                  {advanceId ? (
                    <div className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-4 mb-4 space-y-2">
                      <SummaryRow
                        label="Đã tạm ứng"
                        value={`${formatCurrency(advanceAmount)} ₫`}
                      />
                      <SummaryRow
                        label="Thực chi"
                        value={`${formatCurrency(numericPaymentAmount)} ₫`}
                      />
                      <SummaryRow
                        label={settlementLabel}
                        value={`${formatCurrency(settlementAmount)} ₫`}
                      />
                    </div>
                  ) : null}

                  <div className="space-y-3">
                    <SummaryRow
                      label="Ngân hàng"
                      value={bankName || "Chưa nhập ngân hàng"}
                    />
                    <SummaryRow
                      label="Số tài khoản"
                      value={accountNumber || "Chưa nhập số tài khoản"}
                    />
                    <SummaryRow
                      label="Chủ tài khoản"
                      value={accountHolder || "Chưa nhập"}
                    />
                  </div>
                </div>

                <div className="rounded-[1.75rem] border border-amber-100 bg-amber-50 p-5">
                  <div className="flex items-start gap-3">
                    <Info size={18} className="text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">
                        Lưu ý trước khi gửi
                      </p>
                      <ul className="mt-2 space-y-1.5 text-xs text-amber-700">
                        <li>Tổng đề nghị sẽ tự cộng từ các hạng mục thực tế.</li>
                        <li>Nếu liên kết tạm ứng, app tự tính cần chi thêm hay thu lại.</li>
                        <li>Thông tin tài khoản nên khớp chính xác với người nhận thực tế.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          <div className="px-6 md:px-8 py-5 border-t border-slate-100 flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-slate-50/40">
            <button
              onClick={handleCancel}
              className="px-6 py-3 rounded-2xl font-bold text-slate-500 hover:text-red-600 transition-all"
            >
              Hủy bỏ
            </button>
            <button
              onClick={handleSave}
              disabled={loading || loadingCopy}
              className="px-10 py-3 bg-red-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-red-700 shadow-lg shadow-red-200 transition-all active:scale-95 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Save size={18} />
              )}
              Lưu nháp
            </button>
          </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="rounded-[1.75rem] border border-slate-100 bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-11 w-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
          <Icon size={20} />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
          {description ? (
            <p className="text-sm text-slate-500 mt-1">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function FieldLabel({ label, required = false }) {
  return (
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
  );
}

function SummaryRow({ label, value, dark = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={dark ? "text-white/60 text-xs" : "text-slate-400 text-xs"}>
        {label}
      </span>
      <span
        className={
          dark
            ? "text-right text-sm font-semibold text-white"
            : "text-right text-sm font-semibold text-slate-700"
        }
      >
        {value}
      </span>
    </div>
  );
}

function StatusChip({ icon: Icon, label, highlighted = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold border ${
        highlighted
          ? "bg-red-50 text-red-700 border-red-200"
          : "bg-white text-slate-600 border-slate-200"
      }`}
    >
      <Icon size={14} />
      {label}
    </span>
  );
}